import { createSelector } from 'reselect';
import { getItems } from '@wix/da-shared-react/pkg/Stream/base/selectors';
import { AppState, StudioSection } from '../../types/store';
import { STUDIO_DEVIATIONS_STREAM } from '../../../constants';
import { getStudioSlice, getActiveSection, getStudioParams } from './page';

export const getTableStreamId = createSelector([getStudioSlice], state => state.table.streamId);

export const getTableLayout = createSelector([getStudioSlice], state => state.table.layout);

export const getPageSize = createSelector([getStudioSlice], state => state.table.pageSize);

export const getSelectionState = createSelector([getStudioSlice], state => state.selection);

export const getSelectedRowIndex = createSelector(
  [getSelectionState],
  selection => selection.selectedRows
);

export const getSelectedRowCount = createSelector([getSelectedRowIndex], items => items.length);

export const getDuperbrowseStreamVersion = createSelector(
  [getStudioSlice],
  state => state.duperbrowseStreamVersion
);

export function getSelectedDeviationIds(state: AppState) {
  // sorting makes sure we get the deviation ids in the same order they are on the table
  const rowIndex = getSelectedRowIndex(state).sort((a, b) => a - b);
  const deviationIds = getItems(state, STUDIO_DEVIATIONS_STREAM.streamId);
  // deviation normalizer stores journal ids as composite because of legacy ids
  // we dumbly just split by - and take the id, this will probably break for old shit but backend
  // will just reject it
  return rowIndex.map(idx => {
    const id = deviationIds[idx];
    if (id && id.includes && id.includes('-')) {
      return parseInt(id.split('-')[1]);
    }
    return id;
  });
}

export const getResultsTotalCount = createSelector(
  [getStudioSlice],
  state => state.resultsTotalCount
);

export const getHasResults = createSelector([getResultsTotalCount], count => count > 0);

/**
 * Returns `true` if the user should be able to reorder the rows in the currently active studio section.
 */
export const getIsReorderSupported = createSelector(
  [getActiveSection, getStudioParams],
  (activeSection, { subfolderId }) =>
    [
      StudioSection.DRAFTS,
      StudioSection.LITERATURE,
      StudioSection.JOURNALS,
      StudioSection.PLAYGROUND,
      StudioSection.STASH,
    ].includes(activeSection) ||
    (activeSection === StudioSection.PUBLISHED && !!subfolderId)
);

/**
 * Returns `true` if the user should be able to use drag-and-drop to reorder the rows in the currently active
 * studio section.
 *
 * Separate from {@link getIsReorderSupported} just in case there are more caveats we need to add down the road.
 */
export function getIsDndReorderSupported(state: AppState) {
  return getIsReorderSupported(state);
}
